import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Send, Copy, BookmarkPlus, BellRing, Download, Ban, Trash2, Megaphone, MailX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { GoldButton, GhostButton, Field } from "@/components/atlas/Fields";
import { api, downloadCsv } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { AnnounceDialog } from "@/components/staffing/AnnounceDialog";

export const EventActions = ({ event, reload }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, okMsg) => {
    try {
      const r = await fn();
      if (okMsg) toast.success(okMsg(r.data));
      setDialog(null);
      return r;
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  const publish = (notify) => {
    setBusy(true);
    return run(() => api.post(`/events/${event.id}/publish`, { notify }), (d) => (notify ? `${t("b.published")} · ${d.notified} ${t("b.notified")}` : t("b.publishedSilent"))).then(reload).finally(() => setBusy(false));
  };
  const remind = () => run(() => api.post(`/events/${event.id}/remind`), (d) => `${t("b.remindersSent")} (${d.sent})`);
  const duplicate = () => run(() => api.post(`/events/${event.id}/duplicate`, { date: value || null }), () => t("b.dupOk")).then((r) => r && navigate(`/admin/events/${r.data.id}`));
  const saveTpl = () => run(() => api.post("/templates", { name: value, event_id: event.id }), () => t("b.tplSaved"));
  const cancel = () => run(() => api.post(`/events/${event.id}/cancel`), () => t("es.cancelled")).then(reload);
  const remove = () => run(() => api.delete(`/events/${event.id}`), () => t("b.deleted")).then((r) => r && navigate("/admin/events"));
  const open = (d, v = "") => { setValue(v); setDialog(d); };
  const live = event.status !== "cancelled";

  return (
    <>
      {event.status === "draft" && <GoldButton data-testid="publish-event-btn" onClick={() => setDialog("publish")}><Send className="w-4 h-4" />{t("b.publish")}</GoldButton>}
      {live && <GhostButton data-testid="edit-event-btn" onClick={() => navigate(`/admin/events/${event.id}/edit`)}><Pencil className="w-4 h-4" />{t("c.edit")}</GhostButton>}
      {event.status === "published" && <GhostButton data-testid="remind-event-btn" onClick={remind}><BellRing className="w-4 h-4" />{t("b.remind")}</GhostButton>}
      {event.status === "published" && <GhostButton data-testid="announce-event-btn" onClick={() => setDialog("announce")}><Megaphone className="w-4 h-4" />{t("an.title")}</GhostButton>}
      <GhostButton data-testid="export-event-csv-btn" onClick={() => downloadCsv(event.id).catch((e) => toast.error(errorText(t, e)))}><Download className="w-4 h-4" />CSV</GhostButton>
      <GhostButton data-testid="duplicate-event-btn" onClick={() => open("dup", event.date)}><Copy className="w-4 h-4" /><span className="hidden xl:inline">{t("b.duplicate")}</span></GhostButton>
      <GhostButton data-testid="save-template-btn" onClick={() => open("tpl", event.name)}><BookmarkPlus className="w-4 h-4" /><span className="hidden xl:inline">{t("b.template")}</span></GhostButton>
      {live && <GhostButton data-testid="cancel-event-btn" onClick={() => setDialog("cancel")} title={t("b.cancelEvent")} aria-label={t("b.cancelEvent")} className="hover:!border-red-400 hover:!text-red-600"><Ban className="w-4 h-4" /><span className="hidden xl:inline">{t("b.cancelEvent")}</span></GhostButton>}
      {!live && <GhostButton data-testid="delete-event-btn" onClick={() => setDialog("delete")} className="!border-red-400/60 !text-red-500 hover:!bg-red-500/10"><Trash2 className="w-4 h-4" />{t("b.deleteEvent")}</GhostButton>}

      <Dialog open={dialog === "dup" || dialog === "tpl"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-2xl" data-testid="event-action-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog === "dup" ? t("b.duplicate") : t("b.template")}</DialogTitle></DialogHeader>
          <Field label={dialog === "dup" ? t("b.dupDate") : t("b.tplName")}>
            <Input data-testid="event-action-input" type={dialog === "dup" ? "date" : "text"} value={value} onChange={(e) => setValue(e.target.value)} className="h-10 rounded-none" />
          </Field>
          <DialogFooter>
            <GhostButton onClick={() => setDialog(null)}>{t("c.cancel")}</GhostButton>
            <GoldButton data-testid="event-action-confirm" disabled={!value} onClick={dialog === "dup" ? duplicate : saveTpl}>{t("c.confirm")}</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AnnounceDialog event={event} open={dialog === "announce"} onClose={() => setDialog(null)} />
      <AlertDialog open={dialog === "publish"} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="rounded-2xl sm:max-w-xl" data-testid="publish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("b.publish")}</AlertDialogTitle>
            <AlertDialogDescription>{t("b.publishAsk")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-full" data-testid="publish-dismiss">{t("c.back")}</AlertDialogCancel>
            <GhostButton data-testid="publish-silent-btn" disabled={busy} className="whitespace-nowrap" onClick={() => publish(false)}><MailX className="w-4 h-4" />{t("b.publishSilent")}</GhostButton>
            <GoldButton data-testid="publish-notify-btn" disabled={busy} className="whitespace-nowrap" onClick={() => publish(true)}><Send className="w-4 h-4" />{t("b.publishNotify")}</GoldButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={dialog === "cancel"} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("b.cancelEvent")}</AlertDialogTitle>
            <AlertDialogDescription>{t("b.confirmCancel")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="cancel-event-dismiss">{t("c.back")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-red-700 hover:bg-red-800" data-testid="cancel-event-confirm" onClick={cancel}>{t("b.cancelEvent")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={dialog === "delete"} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-event-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("b.deleteEvent")}</AlertDialogTitle>
            <AlertDialogDescription>{t("b.confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-event-dismiss">{t("c.back")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-red-700 hover:bg-red-800" data-testid="delete-event-confirm" onClick={remove}>{t("b.deleteForever")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
